/**
 * AgentList - Fetches and displays agents with loading/error states
 */

import { useCallback, useEffect, useState } from 'react';
import type { Agent, AgentCategory } from '../types/agent';
import { getAgents, vote, unvote } from '../lib/api';
import { AgentCard } from './AgentCard';
import { AgentCardSkeleton } from './Skeleton';
import { hapticNotification, hapticFeedback } from '../lib/telegram';

interface AgentListProps {
  category: AgentCategory | 'all';
  searchQuery?: string;
  onAgentSelect: (agentId: string) => void;
  onSubmit?: () => void;
  onClearSearch?: () => void;
}

type LoadingState = 'idle' | 'loading' | 'success' | 'error';

function EmptyCategory({ onSubmit }: { onSubmit?: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 animate-screen-fade">
      <div className="relative mb-6">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0088CC]/20 to-[#06B6D4]/20 rounded-full blur-2xl animate-pulse" />
        <div className="relative text-7xl animate-bounce" style={{ animationDuration: '2s' }}>🦞</div>
      </div>
      <h3 className="text-xl font-bold text-[var(--tg-theme-text-color)] mb-2">No apps here yet</h3>
      <p className="text-[var(--tg-theme-hint-color)] text-center mb-6 max-w-xs">
        Be the first to list your app and get discovered by thousands of users!
      </p>
      {onSubmit && (
        <button
          onClick={() => { hapticFeedback('medium'); onSubmit(); }}
          className="px-6 py-3 bg-[#0088CC] hover:brightness-110 text-white font-semibold rounded-xl shadow-lg shadow-[#0088CC]/25 transition-all duration-200 active:scale-95"
        >
          🚀 List Your App
        </button>
      )}
    </div>
  );
}

function EmptySearch({ query, onClear }: { query: string; onClear?: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 animate-screen-fade">
      <div className="relative mb-6">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0088CC]/20 to-[#00d4ff]/20 rounded-full blur-2xl" />
        <div className="relative text-6xl animate-wiggle">🔍</div>
      </div>
      <h3 className="text-xl font-bold text-[var(--tg-theme-text-color)] mb-2">No apps found</h3>
      <p className="text-[var(--tg-theme-hint-color)] text-center mb-2">
        No results for "<span className="text-[var(--tg-theme-text-color)]">{query}</span>"
      </p>
      <p className="text-sm text-[var(--tg-theme-hint-color)] text-center mb-6">
        Try a different search or browse categories
      </p>
      {onClear && (
        <button
          onClick={() => { hapticFeedback('light'); onClear(); }}
          className="px-5 py-2.5 bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)] font-medium rounded-xl transition-all duration-200 active:scale-95"
        >
          ✕ Clear Search
        </button>
      )}
    </div>
  );
}

export function AgentList({ category, searchQuery, onAgentSelect, onSubmit, onClearSearch }: AgentListProps) {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loadingState, setLoadingState] = useState<LoadingState>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const fetchAgents = useCallback(async () => {
    setLoadingState('loading');
    setErrorMessage('');
    try {
      const response = await getAgents({
        category: category === 'all' ? undefined : category,
        sort: 'votes',
        q: searchQuery || undefined,
      });
      setAgents(response.agents);
      setLoadingState('success');
    } catch (err) {
      console.error('Failed to fetch agents:', err);
      setErrorMessage(err instanceof Error ? err.message : 'Failed to load agents');
      setLoadingState('error');
      hapticNotification('error');
    }
  }, [category, searchQuery]);

  useEffect(() => { fetchAgents(); }, [fetchAgents]);

  const handleVote = useCallback(async (agentId: string) => {
    const agent = agents.find((a) => a.id === agentId);
    if (!agent) return;
    const wasVoted = agent.hasVoted;
    setAgents((prev) =>
      prev.map((a) => a.id === agentId
        ? { ...a, hasVoted: !wasVoted, vote_count: wasVoted ? a.vote_count - 1 : a.vote_count + 1 }
        : a
      )
    );
    try {
      const response = wasVoted ? await unvote(agentId) : await vote(agentId);
      setAgents((prev) => prev.map((a) => a.id === agentId ? { ...a, vote_count: response.vote_count } : a));
      hapticNotification('success');
    } catch (err) {
      console.error('Vote failed:', err);
      setAgents((prev) => prev.map((a) => a.id === agentId ? { ...a, hasVoted: wasVoted, vote_count: agent.vote_count } : a));
      hapticNotification('error');
    }
  }, [agents]);

  if (loadingState === 'loading' || loadingState === 'idle') {
    return (
      <div className="space-y-3">
        <AgentCardSkeleton />
        <AgentCardSkeleton />
        <AgentCardSkeleton />
      </div>
    );
  }

  if (loadingState === 'error') {
    return (
      <div className="text-center py-12 animate-screen-fade">
        <p className="text-4xl mb-4">😵</p>
        <p className="font-medium mb-2">Oops! Something went wrong</p>
        <p className="text-sm text-[var(--tg-theme-hint-color)] mb-4">{errorMessage}</p>
        <button onClick={fetchAgents} className="px-4 py-2 bg-[#0088CC] text-white rounded-lg font-medium">
          Try Again
        </button>
      </div>
    );
  }

  if (agents.length === 0) {
    if (searchQuery && searchQuery.trim().length > 0) {
      return <EmptySearch query={searchQuery} onClear={onClearSearch} />;
    }
    return <EmptyCategory onSubmit={onSubmit} />;
  }

  return (
    <div className="space-y-3 card-stagger">
      {agents.map((agent) => (
        <AgentCard key={agent.id} agent={agent} onSelect={onAgentSelect} onVote={handleVote} />
      ))}
    </div>
  );
}
